import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getMemberPoints, getBranchPointSetting } from '../../../../api/member-point';

/**
 * 판매등록 모달의 포인트 사용 로직 (조회 + 계산 + 유효성 검사)
 *
 * - 정식 회원(memberId 존재 && userType === 'member')이고, 지점 포인트 설정이 켜져 있을 때만
 *   포인트 사용 UI를 노출한다 (isPointUsageEnabled).
 * - 보유 포인트는 "회원 보유 포인트 - 이미 장바구니에 담긴 아이템들의 사용 포인트 합"으로 계산한다.
 *   (담기 전 입력 중인 포인트는 아직 카트에 없으므로 제외)
 */
const usePointUsage = ({ memberId, userType, branchId, token, cartItems = [] }) => {
  const isRealMember = !!memberId && userType === 'member';

  // 회원 보유 포인트 잔액 조회
  const { data: memberPointData } = useQuery({
    queryKey: ['memberPointBalance', memberId, branchId],
    // UserPointHistory의 잔액 조회와 동일 파라미터로 통일 (BE 기본값 차이로 인한 불일치 방지)
    queryFn: () => getMemberPoints(memberId, `branchId=${branchId}&type=ALL&page=1&limit=1`, token),
    enabled: isRealMember && !!branchId && !!token,
  });

  // 지점 포인트 설정(사용 on/off, 최소 사용 포인트) 조회
  const { data: pointSetting } = useQuery({
    queryKey: ['branchPointSetting', branchId],
    queryFn: () => getBranchPointSetting(branchId, token),
    enabled: isRealMember && !!branchId && !!token,
  });

  const isPointUsageEnabled = isRealMember && pointSetting?.enabledYn === 'Y';

  const memberPointBalance = memberPointData?.balance || 0;
  // 최소 사용 포인트 (정액 P 전용 — 백엔드와 동일. % 확장 시 minUseType 을 함께 받는다)
  const minUsePoint = pointSetting?.minUsePoint || 0;

  // 이 결제의 유효 최대 사용 포인트 (기준금액 = 포인트 차감 전 결제금액).
  // 백엔드 MemberPointService.resolveMaxUsablePoint 와 동일 규칙.
  const resolveMaxUsable = paymentAmount => {
    if (!pointSetting?.maxUseType || pointSetting?.maxUseValue == null) return Infinity;
    if (pointSetting.maxUseType === 'FIXED') return pointSetting.maxUseValue;
    return Math.floor(((Number(paymentAmount) || 0) * pointSetting.maxUseValue) / 100);
  };

  // 이미 장바구니에 담긴 아이템들이 사용하기로 한 포인트 합계
  const usedPointInCart = useMemo(
    () => cartItems.reduce((sum, item) => sum + (Number(item.usedPoint) || 0), 0),
    [cartItems],
  );

  // 지금 담을 상품에 추가로 사용할 수 있는 남은 포인트
  const availablePoint = Math.max(memberPointBalance - usedPointInCart, 0);

  // 사용할 포인트 입력값 유효성 검사. paymentAmount 는 포인트 차감 전 결제금액.
  // 문제 없으면 null, 있으면 에러 메시지를 반환한다. (백엔드와 동일 규칙 이중 방어)
  const validateUsedPoint = (value, paymentAmount) => {
    const usedPoint = Number(value) || 0;
    if (usedPoint <= 0) return null;

    if (minUsePoint > 0 && usedPoint < minUsePoint) {
      return `최소 ${minUsePoint.toLocaleString()}P부터 사용 가능합니다`;
    }
    const maxUsable = resolveMaxUsable(paymentAmount);

    if (maxUsable !== Infinity && usedPoint > maxUsable) {
      return `이 결제에서 사용할 수 있는 최대 포인트는 ${maxUsable.toLocaleString()}P 입니다`;
    }
    if (usedPoint > availablePoint) {
      return '보유 포인트를 초과할 수 없습니다';
    }
    if (usedPoint > (Number(paymentAmount) || 0)) {
      return '결제금액을 초과할 수 없습니다';
    }
    return null;
  };

  /**
   * 장바구니에 담긴 상품들로 적립될 포인트 총합.
   *
   * 백엔드 MemberPointService.calculateEarnPoint 와 동일한 식이어야 한다:
   * - 적립 기준 금액은 "결제금액 - 사용 포인트"(순결제액)
   * - FIXED: 적립액 고정. 판매등록은 아이템마다 주문(member_order)이 하나씩 생성되므로
   *   상품 N개면 earnValue 가 N번 적립된다.
   * - PERCENT: floor(순결제액 * earnValue / 100)
   * - 순결제액이 0 이하인 아이템은 적립 대상이 아니다.
   */
  const cartEarnPoint = useMemo(() => {
    if (!isPointUsageEnabled || !pointSetting?.earnValue) return 0;

    return cartItems.reduce((sum, item) => {
      const finalPrice = Number(String(item.finalPrice ?? 0).replace(/,/g, '')) || 0;
      const netAmount = finalPrice - (Number(item.usedPoint) || 0);
      if (netAmount <= 0) return sum;

      const earn =
        pointSetting.earnType === 'FIXED'
          ? Math.floor(pointSetting.earnValue)
          : Math.floor((netAmount * pointSetting.earnValue) / 100);
      return sum + earn;
    }, 0);
  }, [isPointUsageEnabled, pointSetting, cartItems]);

  return {
    isPointUsageEnabled,
    memberPointBalance,
    minUsePoint,
    availablePoint,
    cartEarnPoint,
    validateUsedPoint,
    resolveMaxUsable,
  };
};

export default usePointUsage;
